<template>
  <b-form @submit.prevent="onSubmit" novalidate class="label-bold" autocomplete="off">
    <b-tabs v-model="tabIndex" content-class="mt-4">
      <b-tab title="Profile">
        <b-form-group class="text-center">
          <avatar :src="teamPhoto" size="9rem" />
          <input ref="photoFile" type="file" name="avatar" accept="image/*" @change="onPhotoSelect" hidden />
          <b-button
            variant="link"
            class="btn-link-primary"
            v-b-tooltip.hover
            title="Min. 400x400 px. Format: jpg, png or gif. Max. size file is 2MB"
            @click="onPhotoEditClick"
          >
            Edit
          </b-button>
          <b-form-invalid-feedback :state="isValidField('photo')">
            <div v-for="error in errors.photo || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-for="input-name" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Name
          </template>
          <b-form-input id="input-name" v-model="form.name" :state="isValidField('name')" placeholder="Name" />
          <b-form-invalid-feedback :state="isValidField('name')">
            <div v-for="error in errors.name || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-for="input-manager" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Manager
          </template>
          <b-form-select id="input-manager" v-model="form.manager" :state="isValidField('manager')" placeholder="Select manager...">
            <b-form-select-option
              v-for="profile in profilesList"
              :key="profile.id"
              :value="profile.id">
              <template v-if="profile.id!==null">
                {{userName(profile)}}
              </template>
              <template v-else>-</template>
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
        <b-form-group label-for="input-email" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Email
          </template>
          <b-form-input id="input-email" type="email" v-model="form.email" :state="isValidField('email')"  />
          <b-form-invalid-feedback :state="isValidField('email')">
            <div v-for="error in errors.email || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-for="input-department" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Department
          </template>
          <b-form-input id="input-department" v-model="form.department" :state="isValidField('department')" placeholder="Human Resources" />
          <b-form-invalid-feedback :state="isValidField('department')">
            <div v-for="error in errors.department || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-for="input-service" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Service
          </template>
          <b-form-input id="input-service" v-model="form.service" :state="isValidField('service')" placeholder="Support" />
          <b-form-invalid-feedback :state="isValidField('service')">
            <div v-for="error in errors.service || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-tab>

      <b-tab title="About the team">
        <b-form-group label-for="input-facts" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Facts about team
          </template>
          <b-form-textarea id="input-facts" v-model="form.facts" rows="4" :state="isValidField('facts')" placeholder="Facts" />
          <b-form-invalid-feedback :state="isValidField('facts')">
            <div v-for="error in errors.facts || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-for="input-video" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Video
          </template>
          <b-form-file v-model="form.video" accept="video/*" placeholder="Choose a file or drop it here..." drop-placeholder="Drop file here..." />
          <b-form-invalid-feedback :state="isValidField('video')">
            <div v-for="error in errors.video || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-tab>

      <b-tab title="Manage Team Members">
        <manage-team-members :team="team" :profiles="profiles" @change="onChangeMembers" />
      </b-tab>

      <b-tab title="Mission and Values">
        <b-form-row class="justify-content-center mt-5 mb-5">
          <b-col cols="auto">
            <b-button v-if="contract" :href="contract" target="_blank" variant="outline-primary">
              Download Contract
            </b-button>
          </b-col>
        </b-form-row>
        <b-form-group label-for="input-contract" label-cols-sm="4" label-cols-lg="3">
          <template #label>
            Contract
          </template>
          <b-form-file
            id="input-contract"
            v-model="form.contract"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
          />
          <b-form-invalid-feedback :state="isValidField('contract')">
            <div v-for="error in errors.contract || []" :key="error">
              {{ error }}
            </div>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-tab>
    </b-tabs>
    <b-form-row class="justify-content-end mt-5">
      <b-col cols="auto">
        <b-button type="submit" :disabled="submitting" pill variant="primary" class="pl-5 pr-5">
          <template v-if="create">
            Create Team Profile
          </template>
          <template v-else>
            Update Team Profile
          </template>
        </b-button>
      </b-col>
    </b-form-row>
  </b-form>
</template>

<script>
import validator from 'validator'
import MixinForm from './MixinForm'
import Avatar from '@/components/common/Avatar'
import { ManageTeamMembers } from '@/components/TeamProfile'
import { userName, toFormData, readFile } from '@/helpers/helpers'

export default {
  name: 'EditTeamProfileForm',

  mixins: [MixinForm],

  props: {
    team: {
      type: Object,
      default: () => ({})
    },

    create: {
      type: Boolean,
      default: false
    },

    profiles: {
      type: Object,
      default: () => ({})
    },

    activeTab: {
      type: Number,
      default: 0
    }
  },

  watch: {
    team(value) {
      const { photo, contract, video, ...other } = value
      this.form = this.init(other)
      this.teamPhoto = photo
    },

    activeTab(value) {
      this.tabIndex = value
    }
  },

  computed: {
    profilesList() {
      return [{ id: null }, ...Object.values(this.profiles)]
    }
  },

  data() {
    const { photo, contract, video, ...other } = this.team
    const data = this.init(other)

    return {
      form: data,
      teamPhoto: photo,
      contract: contract,
      video: video,
      tabIndex: this.activeTab,
      managemembers: {
        added: [],
        removed: []
      }
    }
  },

  methods: {
    init(team) {
      const { members, ...other } = team
      const manager = members.find(p => p.isManager) || {}
      return { ...other, manager: manager.profile }
    },

    userName(profile) {
      return userName(profile)
    },

    validate(data) {
      const errors = {}
      if (validator.isEmpty(data.name || '')) errors.name = ["Name can't be blank"]
      if (data.email && !validator.isEmail(data.email || '')) errors.email = ['Email is invalid']
      if (data.photo && data.photo.size > 2097152) errors.photo = ['Invalid photo']
      return errors
    },

    prepare(data) {
      return {
        form: toFormData(data),
        managemembers: this.managemembers
      }
    },

    onPhotoSelect({ target }) {
      readFile(target.files[0], event => {
        this.teamPhoto = event.target.result
      })
      this.form.photo = target.files[0]
    },

    onPhotoEditClick() {
      this.$refs.photoFile.click()
    },

    onChangeMembers(value) {
      this.managemembers = value
    }
  },

  components: {
    Avatar,
    ManageTeamMembers
  }
}
</script>
